import { useState } from "react";
import SectionLabel from "@/components/SectionLabel";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { SERVICES } from "@/data/services";
import { CheckCircle2, Video, Building2 } from "lucide-react";

export default function Appointment() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", preferred_date: "", preferred_time: "",
    service: "", mode: "in_clinic", teleconsult_link: "", notes: "", age: "", gender: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.preferred_date || !form.preferred_time) {
      toast.error("Please fill required fields."); return;
    }
    setLoading(true);
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age) : undefined };
      const { data } = await api.post("/appointments", payload);
      setDone(data);
      toast.success("Appointment request received!");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
          <h1 className="mt-6 font-serif text-4xl text-slate-900 tracking-tight">Appointment requested</h1>
          <p className="mt-3 text-slate-600">
            Thank you, <strong>{done.name}</strong>. We've received your request for{" "}
            <strong>{done.preferred_date}</strong> at <strong>{done.preferred_time}</strong>.
            Our team will contact you at <strong>{done.phone}</strong> shortly to confirm.
          </p>
          <div className="mt-6 rounded-2xl border border-slate-200 p-5 bg-slate-50 text-sm text-slate-700">
            <div>Reference ID: <span className="font-mono">{done.id.slice(0, 8)}</span></div>
            <div className="mt-1">Mode: {done.mode === "teleconsult" ? "Teleconsultation" : "In-clinic visit"}</div>
          </div>
          <button onClick={() => setDone(null)} className="mt-6 rounded-full px-5 py-2.5 text-sm font-semibold text-white"
                  style={{ background: "#0A6EBD" }} data-testid="new-appointment-btn">
            Book another appointment
          </button>
        </div>
      </section>
    );
  }

  return (
    <div>
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <SectionLabel>Book Appointment</SectionLabel>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-900">Reserve a time with Dr. Onkar</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">You'll receive a phone confirmation shortly. For urgent needs, please call directly.</p>

          <form onSubmit={submit} className="mt-10 rounded-3xl border border-slate-200/70 p-6 md:p-8 bg-white aarogya-shadow space-y-6"
                data-testid="appointment-form">
            <div className="grid sm:grid-cols-3 gap-3">
              <button type="button" onClick={() => update("mode", "in_clinic")}
                      className={`rounded-2xl border p-4 text-left transition ${form.mode === "in_clinic" ? "border-[#0A6EBD] bg-blue-50/40" : "border-slate-200"}`}
                      data-testid="mode-in-clinic">
                <Building2 className="h-5 w-5 text-[#0A6EBD]" />
                <div className="mt-2 font-semibold text-slate-800">In-clinic visit</div>
                <div className="text-xs text-slate-500">Meet Dr. Onkar at the clinic</div>
              </button>
              <button type="button" onClick={() => update("mode", "teleconsult")}
                      className={`rounded-2xl border p-4 text-left transition ${form.mode === "teleconsult" ? "border-[#0A6EBD] bg-blue-50/40" : "border-slate-200"}`}
                      data-testid="mode-teleconsult">
                <Video className="h-5 w-5 text-[#3F2D86]" />
                <div className="mt-2 font-semibold text-slate-800">Teleconsultation</div>
                <div className="text-xs text-slate-500">Video/audio from home</div>
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full name *"><input required value={form.name} onChange={e=>update("name", e.target.value)} className={input()} data-testid="apt-name" /></Field>
              <Field label="Phone *"><input required value={form.phone} onChange={e=>update("phone", e.target.value)} type="tel" className={input()} data-testid="apt-phone" placeholder="10-digit mobile" /></Field>
              <Field label="Email"><input value={form.email} onChange={e=>update("email", e.target.value)} type="email" className={input()} data-testid="apt-email" /></Field>
              <Field label="Age"><input value={form.age} onChange={e=>update("age", e.target.value)} type="number" min="0" max="120" className={input()} data-testid="apt-age" /></Field>
              <Field label="Gender">
                <select value={form.gender} onChange={e=>update("gender", e.target.value)} className={input()} data-testid="apt-gender">
                  <option value="">Select…</option>
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
              </Field>
              <Field label="Service (optional)">
                <select value={form.service} onChange={e=>update("service", e.target.value)} className={input()} data-testid="apt-service">
                  <option value="">General consultation</option>
                  {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                </select>
              </Field>
              <Field label="Preferred date *"><input required value={form.preferred_date} onChange={e=>update("preferred_date", e.target.value)} type="date" className={input()} data-testid="apt-date" /></Field>
              <Field label="Preferred time *"><input required value={form.preferred_time} onChange={e=>update("preferred_time", e.target.value)} type="time" className={input()} data-testid="apt-time" /></Field>
              {form.mode === "teleconsult" && (
                <Field label="Teleconsultation link (optional)" className="md:col-span-2">
                  <input value={form.teleconsult_link} onChange={e=>update("teleconsult_link", e.target.value)} className={input()} placeholder="Paste your Meet/Zoom link if you have one" data-testid="apt-teleconsult-link" />
                </Field>
              )}
              <Field label="Notes / symptoms" className="md:col-span-2">
                <textarea value={form.notes} onChange={e=>update("notes", e.target.value)} rows={3} className={input()} data-testid="apt-notes"
                          placeholder="Briefly describe what's bothering you or any reports to share." />
              </Field>
            </div>

            <button type="submit" disabled={loading}
                    className="w-full rounded-full px-6 py-3.5 text-white font-semibold aarogya-shadow disabled:opacity-70"
                    style={{ background: "#0A6EBD" }} data-testid="apt-submit-btn">
              {loading ? "Submitting…" : "Request Appointment"}
            </button>
            <p className="text-xs text-slate-500 text-center">By submitting, you agree to be contacted by our clinic for confirmation.</p>
          </form>
        </div>
      </section>
    </div>
  );
}

const input = () =>
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A6EBD] focus:border-transparent";

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
